import { useEffect, useState, useCallback, useRef } from "react";
import { MapContainer, TileLayer, Polygon, Tooltip, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { useGame } from "@/contexts/GameContext";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { useTheme } from "@/contexts/ThemeContext";
import PolygonDrawer, { calcAreaKm2 } from "./PolygonDrawer";
import type { LatLng } from "@/contexts/GameContext";

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

export interface SelectedBlock {
  lat: number;
  lng: number;
  blockId: string;
  polygon: LatLng[];
  areaKm2: number;
  isChecking: boolean;
  isLand: boolean | null;
  oceanRatio?: number;
}

interface WorldMapProps {
  onBlockSelect: (block: SelectedBlock | null) => void;
  drawMode: boolean;
  onDrawComplete: (polygon: LatLng[], areaKm2: number) => void;
  onDrawCancel: () => void;
  flyTarget?: { lat: number; lng: number; zoom: number } | null;
}

// ─── 海判定（Nominatim）────────────────────────────────────────
async function checkOceanRatio(pts: LatLng[]): Promise<number> {
  if (pts.length === 0) return 0;
  // 重心を計算
  const centerLat = pts.reduce((s, p) => s + p.lat, 0) / pts.length;
  const centerLng = pts.reduce((s, p) => s + p.lng, 0) / pts.length;
  // 重心1点だけチェック（ポリゴンは大きいので代表点で判定）
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${centerLat}&lon=${centerLng}&zoom=10&addressdetails=1`,
      { headers: { "Accept-Language": "en" } }
    );
    const data = await res.json();
    if (!data || data.error) return 1;
    const addr = data.address || {};
    const hasCountry = !!(addr.country_code);
    const placeType = (data.type || "").toLowerCase();
    const displayName = (data.display_name || "").toLowerCase();
    const waterKeywords = ["ocean", "sea", "bay", "strait", "gulf", "water", "pacific", "atlantic", "indian", "arctic", "mediterranean", "caribbean"];
    const isWater = waterKeywords.some((kw) => placeType.includes(kw) || displayName.startsWith(kw));
    if (isWater || !hasCountry) return 1;
    return 0;
  } catch {
    return 0;
  }
}

// ─── タイルレイヤー ───────────────────────────────────────────
function DynamicTileLayer({ isDark }: { isDark: boolean }) {
  const map = useMap();
  useEffect(() => {
    map.eachLayer((layer) => { if (layer instanceof L.TileLayer) map.removeLayer(layer); });
    const url = isDark
      ? "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
      : "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";
    L.tileLayer(url, { attribution: "&copy; OpenStreetMap &copy; CARTO", minZoom: 2, maxZoom: 18, noWrap: false }).addTo(map);
    map.getContainer().style.background = isDark ? "#0a0a14" : "#f0f0f5";
  }, [isDark, map]);
  return null;
}

// ─── グリッドレイヤー ─────────────────────────────────────────
function GridLayer({ isDark }: { isDark: boolean }) {
  const map = useMap();
  useEffect(() => {
    const GridTileLayer = L.GridLayer.extend({
      createTile(coords: L.Coords) {
        const tile = document.createElement("canvas");
        const size = (this as L.GridLayer).getTileSize();
        tile.width = size.x; tile.height = size.y;
        const ctx = tile.getContext("2d");
        if (!ctx || coords.z < 5) return tile;
        const alpha = coords.z >= 12 ? (isDark ? 0.4 : 0.3) : coords.z >= 8 ? (isDark ? 0.2 : 0.15) : 0.08;
        ctx.strokeStyle = isDark ? `rgba(251,191,36,${alpha})` : `rgba(180,120,20,${alpha})`;
        ctx.lineWidth = 0.6;
        const subs = coords.z >= 14 ? 8 : coords.z >= 11 ? 4 : 2;
        const cw = size.x / subs; const ch = size.y / subs;
        ctx.beginPath();
        for (let i = 0; i <= subs; i++) {
          ctx.moveTo(i * cw, 0); ctx.lineTo(i * cw, size.y);
          ctx.moveTo(0, i * ch); ctx.lineTo(size.x, i * ch);
        }
        ctx.stroke();
        return tile;
      },
    });
    const layer = new (GridTileLayer as any)({ opacity: 1, zIndex: 400 });
    map.addLayer(layer);
    return () => { map.removeLayer(layer); };
  }, [map, isDark]);
  return null;
}

// ─── 既存ポリゴンクリックハンドラー ──────────────────────────
function ExistingBlockClickHandler({
  blocks,
  drawMode,
  onBlockSelect,
}: {
  blocks: ReturnType<typeof useGame>["blocks"];
  drawMode: boolean;
  onBlockSelect: (b: SelectedBlock | null) => void;
}) {
  useMapEvents({
    click(e) {
      if (drawMode) return;
      // クリック座標がどの既存ポリゴン内か判定
      const { lat, lng } = e.latlng;
      for (const block of Object.values(blocks)) {
        if (!block.polygon?.length) continue;
        if (pointInPolygon({ lat, lng }, block.polygon)) {
          onBlockSelect({
            lat: block.lat,
            lng: block.lng,
            blockId: block.blockId,
            polygon: block.polygon,
            areaKm2: block.areaKm2,
            isChecking: false,
            isLand: true,
            oceanRatio: 0,
          });
          return;
        }
      }
      // 何もない場所をクリック → 選択解除
      onBlockSelect(null);
    },
  });
  return null;
}

function pointInPolygon(pt: LatLng, poly: LatLng[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].lng, yi = poly[i].lat;
    const xj = poly[j].lng, yj = poly[j].lat;
    const intersect = yi > pt.lat !== yj > pt.lat && pt.lng < ((xj - xi) * (pt.lat - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

// ─── マップ移動コンポーネント ─────────────────────────────────
function FlyToController({ target }: { target: { lat: number; lng: number; zoom: number } | null | undefined }) {
  const map = useMap();
  const prevTarget = useRef<typeof target>(null);
  useEffect(() => {
    if (target && target !== prevTarget.current) {
      prevTarget.current = target;
      map.flyTo([target.lat, target.lng], target.zoom, { duration: 1.2 });
    }
  }, [target, map]);
  return null;
}

// ─── メイン WorldMap ──────────────────────────────────────────
export default function WorldMap({ onBlockSelect, drawMode, onDrawComplete, onDrawCancel, flyTarget }: WorldMapProps) {
  const { blocks, landsLoading } = useGame();
  const { user } = useAuth();
  const { lang } = useLang();
  const { isDark } = useTheme();
  const [selected, setSelected] = useState<SelectedBlock | null>(null);
  const [shiftHeld, setShiftHeld] = useState(false);

  // Shiftキー検知
  useEffect(() => {
    const down = (e: KeyboardEvent) => { if (e.key === "Shift") setShiftHeld(true); };
    const up = (e: KeyboardEvent) => { if (e.key === "Shift") setShiftHeld(false); };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); };
  }, []);

  const handleBlockSelect = useCallback((block: SelectedBlock | null) => {
    setSelected(block);
    onBlockSelect(block);
  }, [onBlockSelect]);

  const handleDrawComplete = useCallback(async (polygon: LatLng[], areaKm2: number) => {
    onDrawComplete(polygon, areaKm2);
  }, [onDrawComplete]);

  const getBlockStyle = (block: typeof blocks[string]) => {
    const isMyBlock = user && block.ownerId === user.id;
    if (isMyBlock && block.isForSale) return { color: "#f59e0b", fill: "#f59e0b" };
    if (isMyBlock) return { color: "#22c55e", fill: "#22c55e" };
    if (block.isForSale) return { color: "#f97316", fill: "#f97316" };
    return { color: "#ef4444", fill: "#ef4444" };
  };

  const getTooltipLabel = (block: typeof blocks[string]) => {
    const isMyBlock = user && block.ownerId === user.id;
    if (lang === "ja") {
      if (isMyBlock && block.isForSale) return "🔨 あなたの区画（出品中）";
      if (isMyBlock) return "✅ あなたの区画";
      if (block.isForSale) return "🔨 オークション出品中";
      return "🔒 購入済み区画";
    }
    if (isMyBlock && block.isForSale) return "🔨 Your Block (Auction)";
    if (isMyBlock) return "✅ Your Block";
    if (block.isForSale) return "🔨 Active Auction";
    return "🔒 Owned Block";
  };



  const mapBg = isDark ? "#0a0a14" : "#f0f0f5";
  const hintBg = isDark ? "bg-black/70 border-white/10 text-gray-400" : "bg-white/90 border-gray-200 text-gray-600";

  return (
    <div className="w-full h-full relative">
      <MapContainer
        center={[20, 0]}
        zoom={3}
        minZoom={2}
        maxZoom={18}
        style={{ height: "100%", width: "100%", background: mapBg }}
        zoomControl={true}
        worldCopyJump={true}
        doubleClickZoom={false}
      >
        <TileLayer
          url={isDark
            ? "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            : "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"}
          attribution="&copy; OpenStreetMap &copy; CARTO"
          minZoom={2} maxZoom={18} noWrap={false}
        />
        <DynamicTileLayer isDark={isDark} />
        <GridLayer isDark={isDark} />

        {/* 検索による移動 */}
        <FlyToController target={flyTarget} />

        {/* 既存ポリゴンクリック */}
        <ExistingBlockClickHandler blocks={blocks} drawMode={drawMode} onBlockSelect={handleBlockSelect} />

        {/* ポリゴン描画レイヤー */}
        <PolygonDrawer
          active={drawMode}
          shiftHeld={shiftHeld}
          onComplete={handleDrawComplete}
          onCancel={onDrawCancel}
        />

        {/* 所有区画ポリゴン表示 */}
        {Object.values(blocks).map((block) => {
          if (!block.polygon?.length) return null;
          const style = getBlockStyle(block);
          const isSelected = selected?.blockId === block.blockId;
          return (
            <Polygon
              key={block.blockId}
              positions={block.polygon.map((p) => [p.lat, p.lng])}
              pathOptions={{
                color: isSelected ? "#fbbf24" : style.color,
                weight: isSelected ? 3 : 1.5,
                fillColor: isSelected ? "#fbbf24" : style.fill,
                fillOpacity: isSelected ? 0.35 : 0.3,
              }}
              eventHandlers={{
                click() {
                  if (drawMode) return;
                  handleBlockSelect({
                    lat: block.lat,
                    lng: block.lng,
                    blockId: block.blockId,
                    polygon: block.polygon,
                    areaKm2: block.areaKm2,
                    isChecking: false,
                    isLand: true,
                    oceanRatio: 0,
                  });
                },
              }}
            >
              <Tooltip direction="top" opacity={0.95}>
                <div className="text-xs min-w-[120px]">
                  <p className="font-bold text-sm mb-0.5">{block.ownerName}</p>
                  <p>{getTooltipLabel(block)}</p>
                  <p className="text-gray-400 mt-0.5">{block.areaKm2?.toFixed(4)} km²</p>
                  {block.buildings.length > 0 && (
                    <p className="mt-0.5">
                      {block.buildings.map((b) =>
                        b.type === "house" ? "🏠" : b.type === "company" ? "🏢" : "⚡"
                      ).join(" ")}
                      {" "}{block.buildings.length}{lang === "ja" ? "棟" : " buildings"}
                    </p>
                  )}
                </div>
              </Tooltip>
            </Polygon>
          );
        })}
      </MapContainer>

      {/* 凡例 */}
      <div className={`absolute bottom-4 left-4 backdrop-blur-sm border rounded-xl px-4 py-3 z-[1000] ${hintBg}`}>
        {drawMode ? (
          <div className="space-y-1.5">
            <p className="text-xs font-semibold whitespace-nowrap text-amber-400">
              <i className="ri-edit-line mr-1"></i>
              {lang === "ja" ? "描画モード" : "Draw Mode"}
            </p>
            <p className="text-xs whitespace-nowrap">
              {lang === "ja" ? "クリックで頂点を追加" : "Click to add vertices"}
            </p>
            <p className="text-xs whitespace-nowrap opacity-70">
              {lang === "ja" ? "Shift: 直線補助" : "Shift: straight line"}
            </p>
            <p className="text-xs whitespace-nowrap opacity-70">
              {lang === "ja" ? "始点クリック or ダブルクリックで確定" : "Click start or dbl-click to finish"}
            </p>
            <p className="text-xs whitespace-nowrap opacity-70">
              {lang === "ja" ? "ESC: キャンセル" : "ESC: cancel"}
            </p>
          </div>
        ) : (
          <>
            <p className="text-xs font-semibold mb-2 whitespace-nowrap">
              <i className="ri-map-2-line text-amber-400 mr-1"></i>
              {lang === "ja" ? "土地を購入するには描画ボタンを押す" : "Press Draw to purchase land"}
            </p>
            <div className="space-y-1">
              {[
                { color: "bg-green-500", label: lang === "ja" ? "あなたの区画" : "Your block" },
                { color: "bg-red-500", label: lang === "ja" ? "他のプレイヤー" : "Others' block" },
                { color: "bg-amber-400", label: lang === "ja" ? "オークション中" : "On auction" },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2">
                  <span className={`w-3 h-3 rounded-sm ${item.color} flex-shrink-0`}></span>
                  <span className="text-xs whitespace-nowrap">{item.label}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Shiftスナップ表示 */}
      {drawMode && shiftHeld && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-[1100] pointer-events-none">
          <div className="bg-amber-400/90 text-black text-xs font-bold px-3 py-1.5 rounded-full whitespace-nowrap animate-btn-pop">
            <i className="ri-focus-3-line mr-1"></i>
            {lang === "ja" ? "直線スナップ ON" : "Straight Snap ON"}
          </div>
        </div>
      )}

      {/* 描画モード中のパルスリング */}
      {drawMode && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1100] pointer-events-none">
          <div className="flex items-center gap-2 bg-amber-400/95 text-black text-xs font-bold px-4 py-2 rounded-full whitespace-nowrap animate-slide-up-fade">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-black opacity-60"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-black"></span>
            </span>
            {lang === "ja" ? "描画モード — クリックで頂点を追加" : "Draw Mode — Click to add vertices"}
          </div>
        </div>
      )}
    </div>
  );
}
